// ============================================
// SLOT DEFINITIONS
// ============================================
// This file defines slot types and their characteristics for the slot-based layout system.
// Each slot type has:
// - Purpose: What the slot is meant to contain
// - Suggested components: Recommended component types for this slot
// - Icon: Visual indicator in the placeholder
// - Max components: Limit for how many components can be in this slot
// 
// These definitions are used by:
// - SlotPlaceholder: To show appropriate suggestions
// - ComponentSelector: To filter components by slot type
// - PreviewRenderer: To enforce slot constraints
// ============================================

/**
 * Slot type definitions with component suggestions and constraints
 */
export const SLOT_TYPES = {
  // ============================================
  // GENERIC LAYOUT SLOTS (used in basic layouts)
  // ============================================
  header: {
    id: 'header',
    name: 'Header',
    purpose: 'Page header section',
    icon: '📰',
    suggestedComponents: ['header', 'image', 'text', 'carousel'],
    primaryComponents: ['header', 'image'],
    maxComponents: 3,
    description: 'Header section for your page',
    tips: 'Use headers, images, or carousels for impactful page openings',
    category: 'content',
    spacing: {
      internal: 16,  // Gap between header components
      external: 28   // Gap from header to content below
    }
  },

  content: {
    id: 'content',
    name: 'Content',
    purpose: 'Main content area',
    icon: '📄',
    suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'table', 'list'],
    primaryComponents: ['paragraph', 'card'],
    maxComponents: 20,
    description: 'Main content area for your page',
    tips: 'Add paragraphs, cards, images, and forms to build your content',
    category: 'content',
    spacing: {
      internal: 20,  // Comfortable reading space
      external: 28   // Gap to footer or next section
    }
  },

  hero: {
    id: 'hero',
    name: 'Hero Section',
    purpose: 'Hero/banner section',
    icon: '🎯',
    suggestedComponents: ['image', 'carousel', 'header', 'card', 'video'],
    primaryComponents: ['image', 'carousel'],
    maxComponents: 1,
    description: 'Hero section for landing pages',
    tips: 'Use large images, videos, or carousels for maximum impact',
    category: 'content',
    spacing: {
      internal: 0,   // Single component - no internal gap
      external: 32   // Large gap from hero to content
    }
  },

  'main-content': {
    id: 'main-content',
    name: 'Main Content',
    purpose: 'Primary content area',
    icon: '📄',
    suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'table'],
    primaryComponents: ['paragraph', 'card'],
    maxComponents: 15,
    description: 'Main content section',
    tips: 'Build your primary content with text, cards, and images',
    category: 'content',
    spacing: {
      internal: 20,  // Comfortable spacing
      external: 24   // Gap to adjacent sections
    }
  },

  'left-column': {
    id: 'left-column',
    name: 'Left Column',
    purpose: 'Left column content',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'form', 'list'],
    primaryComponents: ['card', 'text'],
    maxComponents: 10,
    description: 'Left column content area',
    tips: 'Add cards, images, or text content',
    category: 'content',
    spacing: {
      internal: 18,  // Comfortable column spacing
      external: 24   // Gap between columns handled by layout
    }
  },

  'right-column': {
    id: 'right-column',
    name: 'Right Column',
    purpose: 'Right column content',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'form', 'list'],
    primaryComponents: ['card', 'text'],
    maxComponents: 10,
    description: 'Right column content area',
    tips: 'Add cards, images, or text content',
    category: 'content',
    spacing: {
      internal: 18,  // Comfortable column spacing
      external: 24   // Gap between columns handled by layout
    }
  },

  'column-1': {
    id: 'column-1',
    name: 'Column 1',
    purpose: 'First column',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button'],
    primaryComponents: ['card'],
    maxComponents: 5,
    description: 'First column in multi-column layout',
    category: 'content',
    spacing: {
      internal: 16,
      external: 20
    }
  },

  'column-2': {
    id: 'column-2',
    name: 'Column 2',
    purpose: 'Second column',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button'],
    primaryComponents: ['card'],
    maxComponents: 5,
    description: 'Second column in multi-column layout',
    category: 'content',
    spacing: {
      internal: 16,
      external: 20
    }
  },

  'column-3': {
    id: 'column-3',
    name: 'Column 3',
    purpose: 'Third column',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button'],
    primaryComponents: ['card'],
    maxComponents: 5,
    description: 'Third column in multi-column layout',
    category: 'content',
    spacing: {
      internal: 16,
      external: 20
    }
  },

  'column-4': {
    id: 'column-4',
    name: 'Column 4',
    purpose: 'Fourth column',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button'],
    primaryComponents: ['card'],
    maxComponents: 5,
    description: 'Fourth column in multi-column layout',
    category: 'content',
    spacing: {
      internal: 16,
      external: 20
    }
  },

  'grid-col-1': {
    id: 'grid-col-1',
    name: 'Grid Column 1',
    purpose: 'First grid column',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button'],
    primaryComponents: ['card'],
    maxComponents: 3,
    category: 'content',
    spacing: { internal: 14, external: 20 }
  },

  'grid-col-2': {
    id: 'grid-col-2',
    name: 'Grid Column 2',
    purpose: 'Second grid column',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button'],
    primaryComponents: ['card'],
    maxComponents: 3,
    category: 'content',
    spacing: { internal: 14, external: 20 }
  },

  'grid-col-3': {
    id: 'grid-col-3',
    name: 'Grid Column 3',
    purpose: 'Third grid column',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button'],
    primaryComponents: ['card'],
    maxComponents: 3,
    category: 'content',
    spacing: { internal: 14, external: 20 }
  },

  // ============================================
  // HEADER & BRANDING SLOTS
  // ============================================
  logo: {
    id: 'logo',
    name: 'Logo Area',
    purpose: 'Brand identity and logo placement',
    icon: '🏷️',
    suggestedComponents: ['image', 'text', 'header'],
    primaryComponents: ['image'], // Most common choice
    maxComponents: 1,
    description: 'Add your company logo or brand mark',
    tips: 'Use a transparent PNG for best results. Recommended size: 200x60px',
    category: 'branding',
    spacing: {
      internal: 12,  // Gap between components inside logo slot (if multiple allowed)
      external: 24   // Gap from logo to next section
    }
  },

  navigation: {
    id: 'navigation',
    name: 'Navigation',
    purpose: 'Site navigation and menu',
    icon: '🧭',
    suggestedComponents: ['breadcrumb', 'button', 'list', 'header'],
    primaryComponents: ['breadcrumb', 'list'],
    maxComponents: 1,
    allowMultiple: false,
    description: 'Main navigation menu for your site',
    tips: 'Use breadcrumb for hierarchical navigation or list for menu items',
    category: 'navigation',
    spacing: {
      internal: 16,  // Gap between navigation items
      external: 20   // Gap from navigation to next section
    }
  },

  'header-banner': {
    id: 'header-banner',
    name: 'Header/Banner',
    purpose: 'Hero section or page header',
    icon: '🎯',
    suggestedComponents: ['image', 'carousel', 'header', 'card', 'video'],
    primaryComponents: ['image', 'carousel'],
    maxComponents: 1,
    description: 'Eye-catching header or banner area',
    tips: 'Use high-quality images (1920x600px) or carousels for dynamic content',
    category: 'content',
    spacing: {
      internal: 0,   // Single component - no internal gap needed
      external: 32   // Large gap from hero to next section
    }
  },

  // ============================================
  // CONTENT SLOTS
  // ============================================
  body: {
    id: 'body',
    name: 'Body Area',
    purpose: 'Main content area',
    icon: '📄',
    suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'table', 'list', 'video'],
    primaryComponents: ['paragraph', 'card'],
    maxComponents: 10, // Allow multiple content blocks
    allowMultiple: true,
    description: 'Primary content area for your page',
    tips: 'Mix text, images, and cards for engaging content',
    category: 'content',
    spacing: {
      internal: 20,  // Comfortable reading space between paragraphs/cards
      external: 28   // Large gap from header/sidebar
    }
  },

  intro: {
    id: 'intro',
    name: 'Intro Text Area',
    purpose: 'Introduction or summary text',
    icon: '📝',
    suggestedComponents: ['text', 'paragraph', 'header', 'card'],
    primaryComponents: ['paragraph', 'header'],
    maxComponents: 2,
    description: 'Introduce your page content with compelling text',
    tips: 'Keep it concise - 2-3 paragraphs maximum',
    category: 'content',
    spacing: {
      internal: 16,  // Moderate spacing between intro paragraphs
      external: 24   // Gap from intro to main content
    }
  },

  // ============================================
  // SIDEBAR SLOTS
  // ============================================
  sidebar: {
    id: 'sidebar',
    name: 'Side Bar',
    purpose: 'Secondary navigation or content',
    icon: '📌',
    suggestedComponents: ['list', 'card', 'button', 'form', 'text'],
    primaryComponents: ['list', 'card'],
    maxComponents: 5,
    allowMultiple: true,
    description: 'Sidebar for secondary navigation or widgets',
    tips: 'Use for navigation links, quick actions, or supplementary content',
    category: 'navigation',
    spacing: {
      internal: 12,  // Compact sidebar items
      external: 16   // Moderate gap from main content
    }
  },

  'side-navigation': {
    id: 'side-navigation',
    name: 'Side Navigation',
    purpose: 'Vertical navigation menu',
    icon: '🗂️',
    suggestedComponents: ['list', 'button', 'breadcrumb', 'accordion'],
    primaryComponents: ['list'],
    maxComponents: 1,
    description: 'Vertical navigation menu for sections',
    tips: 'Use list component with links for clean navigation',
    category: 'navigation',
    spacing: {
      internal: 14,  // Comfortable spacing for nav items
      external: 20   // Gap from side nav to main content
    }
  },

  // ============================================
  // CONTENT BOX SLOTS
  // ============================================
  box1: {
    id: 'box1',
    name: 'Box 1',
    purpose: 'First content box',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button', 'icon'],
    primaryComponents: ['card'],
    maxComponents: 1,
    description: 'First featured content box',
    tips: 'Cards work best for boxed content with images and text',
    category: 'content',
    spacing: {
      internal: 12,  // Tight spacing within box content
      external: 20   // Gap between boxes in grid
    }
  },

  box2: {
    id: 'box2',
    name: 'Box 2',
    purpose: 'Second content box',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button', 'icon'],
    primaryComponents: ['card'],
    maxComponents: 1,
    description: 'Second featured content box',
    tips: 'Keep consistent with Box 1 for visual harmony',
    category: 'content',
    spacing: {
      internal: 12,  // Tight spacing within box content
      external: 20   // Gap between boxes in grid
    }
  },

  box3: {
    id: 'box3',
    name: 'Box 3',
    purpose: 'Third content box',
    icon: '📦',
    suggestedComponents: ['card', 'image', 'text', 'button', 'icon'],
    primaryComponents: ['card'],
    maxComponents: 1,
    description: 'Third featured content box',
    tips: 'Maintain design consistency across all boxes',
    category: 'content',
    spacing: {
      internal: 12,  // Tight spacing within box content
      external: 20   // Gap between boxes in grid
    }
  },

  // ============================================
  // GALLERY & MEDIA SLOTS
  // ============================================
  gallery: {
    id: 'gallery',
    name: 'Gallery Grid',
    purpose: 'Image or card gallery',
    icon: '🖼️',
    suggestedComponents: ['carousel', 'image', 'card', 'video'],
    primaryComponents: ['image', 'carousel'],
    maxComponents: 12,
    allowMultiple: true,
    description: 'Display multiple images or cards in a grid',
    tips: 'Use uniform image sizes for best grid appearance',
    category: 'media',
    gridDisplay: true, // Indicates this slot uses grid layout for children
    spacing: {
      internal: 16,  // Grid gap between gallery items
      external: 28   // Gap from gallery to next section
    }
  },

  // ============================================
  // FOOTER SLOTS
  // ============================================
  footer: {
    id: 'footer',
    name: 'Footer',
    purpose: 'Page footer content',
    icon: '🔻',
    suggestedComponents: ['text', 'paragraph', 'button', 'list', 'breadcrumb'],
    primaryComponents: ['text', 'list'],
    maxComponents: 5,
    allowMultiple: true,
    description: 'Footer area for copyright, links, and info',
    tips: 'Include copyright, privacy links, and social media',
    category: 'footer',
    spacing: {
      internal: 14,  // Moderate spacing between footer items
      external: 24   // Gap from footer to page edge
    }
  }
};

/**
 * Slot categories for grouping and filtering
 */
export const SLOT_CATEGORIES = {
  branding: {
    name: 'Branding',
    description: 'Logo and brand identity',
    color: '#8B5CF6'
  },
  navigation: {
    name: 'Navigation',
    description: 'Menus and navigation elements',
    color: '#3B82F6'
  },
  content: {
    name: 'Content',
    description: 'Main page content areas',
    color: '#10B981'
  },
  media: {
    name: 'Media',
    description: 'Images, videos, and galleries',
    color: '#F59E0B'
  },
  footer: {
    name: 'Footer',
    description: 'Footer content',
    color: '#6B7280'
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get slot definition by slot ID
 * @param {string} slotId - Slot identifier
 * @returns {Object|null} Slot definition or null if not found
 */
export const getSlotDefinition = (slotId) => {
  return SLOT_TYPES[slotId] || null;
};

/**
 * Get suggested components for a slot type
 * @param {string} slotId - Slot identifier
 * @returns {Array} Array of suggested component types
 */
export const getSlotSuggestedComponents = (slotId) => {
  const slot = getSlotDefinition(slotId);
  return slot?.suggestedComponents || [];
};

/**
 * Get primary (most recommended) components for a slot
 * @param {string} slotId - Slot identifier
 * @returns {Array} Array of primary component types
 */
export const getSlotPrimaryComponents = (slotId) => {
  const slot = getSlotDefinition(slotId);
  return slot?.primaryComponents || slot?.suggestedComponents || [];
};

/**
 * Get all slots by category
 * @param {string} category - Category name
 * @returns {Array} Array of slot definitions in that category
 */
export const getSlotsByCategory = (category) => {
  return Object.values(SLOT_TYPES).filter(slot => slot.category === category);
};

/**
 * Check if a component type is suggested for a slot
 * @param {string} slotId - Slot identifier
 * @param {string} componentType - Component type to check
 * @returns {boolean} True if component is suggested
 */
export const isComponentSuggestedForSlot = (slotId, componentType) => {
  const suggestions = getSlotSuggestedComponents(slotId);
  return suggestions.includes(componentType);
};

/**
 * Get slot icon
 * @param {string} slotId - Slot identifier
 * @returns {string} Emoji icon for the slot
 */
export const getSlotIcon = (slotId) => {
  const slot = getSlotDefinition(slotId);
  return slot?.icon || '📦';
};

/**
 * Get maximum components allowed in a slot
 * @param {string} slotId - Slot identifier
 * @returns {number} Max components (default: 1)
 */
export const getSlotMaxComponents = (slotId) => {
  const slot = getSlotDefinition(slotId);
  return slot?.maxComponents || 1;
};

/**
 * Check if slot allows multiple components
 * @param {string} slotId - Slot identifier
 * @returns {boolean} True if multiple components allowed
 */
export const doesSlotAllowMultiple = (slotId) => {
  const slot = getSlotDefinition(slotId);
  return slot?.allowMultiple === true || (slot?.maxComponents || 1) > 1;
};

/**
 * Get slot tips/guidance text
 * @param {string} slotId - Slot identifier
 * @returns {string} Tips for using the slot
 */
export const getSlotTips = (slotId) => {
  const slot = getSlotDefinition(slotId);
  return slot?.tips || 'Add components to this slot';
};

/**
 * Get all slot types
 * @returns {Object} All slot type definitions
 */
export const getAllSlotTypes = () => SLOT_TYPES;

/**
 * Get slot category info
 * @param {string} category - Category identifier
 * @returns {Object|null} Category information
 */
export const getSlotCategory = (category) => {
  return SLOT_CATEGORIES[category] || null;
};

/**
 * Get slot spacing configuration
 * @param {string} slotId - Slot identifier
 * @returns {Object} Spacing configuration { internal, external }
 */
export const getSlotSpacing = (slotId) => {
  const slot = getSlotDefinition(slotId);
  return slot?.spacing || { internal: 16, external: 24 }; // Default values
};

/**
 * Get internal spacing for a slot (gap between components within the slot)
 * @param {string} slotId - Slot identifier
 * @returns {number} Internal spacing in pixels
 */
export const getSlotInternalSpacing = (slotId) => {
  const spacing = getSlotSpacing(slotId);
  return spacing.internal || 16;
};

/**
 * Get external spacing for a slot (gap from slot to next section)
 * @param {string} slotId - Slot identifier
 * @returns {number} External spacing in pixels
 */
export const getSlotExternalSpacing = (slotId) => {
  const spacing = getSlotSpacing(slotId);
  return spacing.external || 24;
};












